import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo, useEffect, forwardRef, useRef, useState, useContext } from 'react';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { CriticalIcon, CheckmarkBackgroundIcon } from '@paypalcorp/pp-react-icons';
import { css, useTheme, jsx, ClassNames } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math, remToPx } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';
import { devWarning, utilStyles } from '@paypalcorp/pp-react-utils';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { IntlContext, getFormattedMessage } from '@paypalcorp/pp-react-intl';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19, _templateObject20, _templateObject21, _templateObject22, _templateObject23, _templateObject24, _templateObject25, _templateObject26, _templateObject27, _templateObject28, _templateObject29, _templateObject30, _templateObject31, _templateObject32, _templateObject33, _templateObject34, _templateObject35, _templateObject36, _templateObject37, _templateObject38, _templateObject39, _templateObject40, _templateObject41, _templateObject42, _templateObject43, _templateObject44, _templateObject45, _templateObject46, _templateObject47, _templateObject48, _templateObject49, _templateObject50;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
      sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis,
      sysBorderRadiusSm = tokens.sysBorderRadiusSm,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
      sysSpaceTiny = tokens.sysSpaceTiny,
      sysSpaceXs = tokens.sysSpaceXs,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceXl = tokens.sysSpaceXl,
      sysMotionDurationMoveSmall = tokens.sysMotionDurationMoveSmall,
      sysSizeTapTargetDefault = tokens.sysSizeTapTargetDefault,
      sysColorStructureBorderHighContrast = tokens.sysColorStructureBorderHighContrast,
      sysColorErrorMain = tokens.sysColorErrorMain,
      sysColorSuccessMain = tokens.sysColorSuccessMain,
      sysTypographyBodyLineHeight = tokens.sysTypographyBodyLineHeight,
      sysTypographyCaptionLineHeight = tokens.sysTypographyCaptionLineHeight,
      sysColorBackgroundHighContrast = tokens.sysColorBackgroundHighContrast,
      sysSizeFieldLg = tokens.sysSizeFieldLg;
  var backgroundSpaceTop = math("(".concat(sysSizeFieldLg, " - ").concat(sysTypographyBodyLineHeight, ") / 2"));
  var backgroundSpaceBottom = math("(".concat(sysSizeFieldLg, " - ").concat(sysTypographyBodyLineHeight, ") / 2")); // 64px - 8px - 20px - 2px - 24px

  var backgroundSpaceBottomEntered = math("".concat(sysSizeFieldLg, " - ").concat(sysSpaceSm, " - ").concat(sysTypographyCaptionLineHeight, " - ").concat(sysSpaceTiny, " - ").concat(sysTypographyBodyLineHeight));
  var ppuiV2CurrencyInputMinHeight = math('4rem - 0.125rem'); //subtract borders in currency input

  var _utilStyles = utilStyles(tokens),
      focusStyles = _utilStyles.focusStyles,
      ellipsis = _utilStyles.ellipsis,
      screenReader = _utilStyles.screenReader,
      variables = _utilStyles.variables;

  var _textStyles = textStyles(tokens),
      caption = _textStyles.caption,
      body = _textStyles.body,
      fieldLabelSm = _textStyles.fieldLabelSm;

  var focusOffsetElement = focusStyles.focusOffsetElement;
  var ppuiEaseMoveSmall = variables.ppuiEaseMoveSmall;

  function info() {
    return css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n      ", ";\n      display: flex;\n      margin: ", " 0 0;\n    "])), caption({
      color: sysColorBackgroundMediumContrast
    }), sysSpaceXs);
  }

  function fieldIcon() {
    return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n      text-decoration: none;\n      position: absolute;\n    "])));
  }

  var label = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: label;\n    ", "\n    ", ";\n    pointer-events: none;\n    position: absolute;\n    top: ", ";\n    transition: font-size ", " ", ",\n      top ", " ", ",\n      background-color ", " ", ";\n    right: ", ";\n    left: ", ";\n    background-clip: padding-box;\n  "])), fieldLabelSm({
    color: sysColorBackgroundMediumContrast
  }), ellipsis, sysSpaceSm, sysMotionDurationMoveSmall, ppuiEaseMoveSmall, sysMotionDurationMoveSmall, ppuiEaseMoveSmall, sysMotionDurationMoveSmall, ppuiEaseMoveSmall, sysSpaceMd, sysSpaceMd);
  var labelWhenPlaceholderShownAndInputNotFocused = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: label_placeholder_shown_and_not_focused;\n    :placeholder-shown:not(:focus) ~ label {\n      ", ";\n      top: ", ";\n      background-color: transparent;\n      margin-bottom: 0;\n    }\n  "])), body({
    color: sysColorBackgroundMediumContrast
  }), backgroundSpaceTop);
  var labelWithScreenreader = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: label_screenreader;\n    ", "\n  "])), screenReader);
  var helperText = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: helper_text;\n    ", "\n    display: block;\n    list-style: none;\n    padding: 0;\n    overflow-wrap: break-word;\n  "])), info());
  var base = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: text_input_base;\n    ", ";\n    padding: 0;\n    position: relative;\n    text-align: left;\n    [dir='rtl'] & {\n      text-align: right;\n    }\n  "])), body());
  var iconRight = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: icon_right;\n    ", "\n    width: ", ";\n    height: ", ";\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    top: ", ";\n    right: ", ";\n    left: auto;\n    [dir='rtl'] & {\n      left: ", ";\n      right: auto;\n    }\n  "])), fieldIcon(), sysSizeTapTargetDefault, sysSizeTapTargetDefault, sysSpaceSm, sysSpaceSm, sysSpaceSm);
  var iconLeft = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: icon_left;\n    pointer-events: none;\n    z-index: 1;\n    ", "\n    inset: ", " auto ", " ", ";\n    color: ", ";\n    [dir='rtl'] & {\n      inset: ", " ", " ", " auto;\n    }\n  "])), fieldIcon(), backgroundSpaceTop, backgroundSpaceTop, sysSpaceMd, sysColorBackgroundMediumContrast, backgroundSpaceTop, sysSpaceMd, backgroundSpaceTop);
  var control = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: text_input_control;\n    padding: ", "\n      ", "\n      ", "\n      ", ";\n    ", ";\n    text-overflow: ellipsis;\n    box-sizing: border-box;\n    width: 100%;\n    border: ", " solid\n      ", ";\n    border-radius: ", ";\n    background-color: ", ";\n    &::placeholder {\n      color: ", ";\n    }\n    &:placeholder-shown:not(:focus)::-webkit-input-placeholder {\n      opacity: 0;\n      transition: opacity ", " ", ";\n    }\n    &:placeholder-shown:not(:focus)::-moz-placeholder {\n      opacity: 0;\n      transition: opacity ", " ", ";\n    }\n    &:first-line {\n      line-height: ", ";\n    }\n    &::-ms-reveal,\n    &::-ms-clear {\n      display: none;\n    }\n\n    margin: 0;\n    outline: none;\n    position: relative;\n\n    &:focus {\n      ", ";\n    }\n\n    -webkit-appearance: none;\n    -moz-appearance: none;\n\n    min-height: ", "; // Replace later\n    &::-webkit-input-placeholder {\n      color: ", ";\n      opacity: 1;\n    }\n    &:-moz-placeholder {\n      color: ", ";\n      opacity: 1;\n      transition: opacity ", " ", ";\n    }\n  "])), math("".concat(backgroundSpaceTop, " + ").concat(sysSpaceSm)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessNormal)), backgroundSpaceBottomEntered, math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessNormal)), body(), sysColorStructureBorderHighContrast, sysBorderThicknessNormal, sysBorderRadiusSm, sysColorBackgroundSurface, sysColorBackgroundMediumContrast, sysMotionDurationMoveSmall, ppuiEaseMoveSmall, sysMotionDurationMoveSmall, ppuiEaseMoveSmall, sysTypographyBodyLineHeight, focusOffsetElement, sysSizeFieldLg, sysColorBackgroundMediumContrast, sysColorBackgroundMediumContrast, sysMotionDurationMoveSmall, ppuiEaseMoveSmall);
  var nolabelControl = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: no_label_text_input_control;\n    padding: ", "\n      ", "\n      ", "\n      ", ";\n    &:placeholder-shown:not(:focus)::placeholder {\n      opacity: 1;\n    }\n  "])), math("".concat(backgroundSpaceTop, " - ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessNormal)), math("".concat(backgroundSpaceBottom, " - ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessNormal)));
  var errorControl = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: error_text_input_control;\n    border: ", " solid ", ";\n    padding: ", "\n      ", "\n      ", "\n      ", ";\n  "])), sysBorderThicknessEmphasis, sysColorErrorMain, math("".concat(backgroundSpaceTop, " + ").concat(sysSpaceSm, " - ").concat(sysBorderThicknessEmphasis, " / 2")), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(backgroundSpaceBottomEntered, " - ").concat(sysBorderThicknessEmphasis, " / 2")), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessEmphasis)));
  var controlErrorWithIconRight = css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    label: text_input_control_error_with_icon_right;\n    padding-right: ", ";\n    padding-left: ", ";\n    [dir='rtl'] & {\n      padding-left: ", ";\n      padding-right: ", ";\n    }\n  "])), math("".concat(sysSpaceMd, " +\n      ").concat(sysSizeTapTargetDefault, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " -\n      ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " +\n          ").concat(sysSizeTapTargetDefault, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " -\n        ").concat(sysBorderThicknessEmphasis)));
  var controlErrorWithIconLeft = css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    label: text_input_control_error_with_icon_left;\n    padding-left: ", ";\n    padding-right: ", ";\n    [dir='rtl'] & {\n      padding-right: ", ";\n      padding-left: ", ";\n    }\n  "])), math("2 * ".concat(sysSpaceMd, " + ").concat(sysSpaceXl, " -\n      ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " -\n      ").concat(sysBorderThicknessEmphasis)), math("2 * ".concat(sysSpaceMd, " + ").concat(sysSpaceXl, " -\n          ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " -\n          ").concat(sysBorderThicknessEmphasis)));
  var controlErrorWithIconLeftAndRight = css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    label: text_input_control_error_with_icon_left_and_right;\n    padding-right: ", ";\n    padding-left: ", ";\n    [dir='rtl'] & {\n      padding-right: ", ";\n      padding-left: ", ";\n    }\n  "])), math("".concat(sysSpaceMd, " +\n      ").concat(sysSizeTapTargetDefault, " - ").concat(sysBorderThicknessEmphasis)), math("2 * ".concat(sysSpaceMd, " + ").concat(sysSpaceXl, " -\n      ").concat(sysBorderThicknessEmphasis)), math("2 * ".concat(sysSpaceMd, " + ").concat(sysSpaceXl, " -\n          ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " +\n          ").concat(sysSizeTapTargetDefault, " - ").concat(sysBorderThicknessEmphasis)));
  var noLabelControlError = css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    label: no_label_text_input_control_error;\n    padding: ", "\n      ", "\n      ", "\n      ", ";\n  "])), math("".concat(backgroundSpaceTop, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(backgroundSpaceBottom, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessEmphasis)));
  var controlWithIconRight = css(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n    label: text_input_control_with_icon_right;\n    padding-right: ", ";\n    padding-left: ", ";\n    [dir='rtl'] & {\n      padding-left: ", ";\n      padding-right: ", ";\n    }\n  "])), math("".concat(sysSpaceMd, " + ").concat(sysSpaceSm, " + ").concat(sysSizeTapTargetDefault, " - ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " + ").concat(sysSpaceSm, " + ").concat(sysSizeTapTargetDefault, " - ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessNormal)));
  var controlWithIconLeft = css(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n    label: text_input_control_with_icon_left;\n    padding-left: ", ";\n    padding-right: ", ";\n    [dir='rtl'] & {\n      padding-right: ", ";\n      padding-left: ", ";\n    }\n  "])), math("2 * ".concat(sysSpaceMd, " + ").concat(sysSpaceXl, " -\n      ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " -\n      ").concat(sysBorderThicknessNormal)), math("2 * ".concat(sysSpaceMd, " + ").concat(sysSpaceXl, " -\n        ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " -\n        ").concat(sysBorderThicknessNormal)));
  var controlWithIconLeftAndRight = css(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n    label: text_input_control_with_icon_left_and_right;\n    padding-left: ", ";\n    padding-right: ", ";\n    [dir='rtl'] & {\n      padding-right: ", ";\n      padding-left: ", ";\n    }\n  "])), math("2 * ".concat(sysSpaceMd, " + ").concat(sysSpaceXl, " -\n        ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " +\n        ").concat(sysSizeTapTargetDefault, " - ").concat(sysBorderThicknessNormal)), math("2 * ".concat(sysSpaceMd, " + ").concat(sysSpaceXl, " -\n        ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " +\n          ").concat(sysSizeTapTargetDefault, " - ").concat(sysBorderThicknessNormal)));
  var labelWithIconRight = css(_templateObject20 || (_templateObject20 = _taggedTemplateLiteral(["\n    label: label_with_icon_right;\n    left: ", ";\n    right: ", ";\n    [dir='rtl'] & {\n      left: ", ";\n      right: ", ";\n    }\n  "])), sysSpaceMd, math("".concat(sysSpaceMd, " +\n        ").concat(sysSizeTapTargetDefault)), math("".concat(sysSpaceMd, " + ").concat(sysSizeTapTargetDefault)), sysSpaceMd);
  var labelWithIconLeft = css(_templateObject21 || (_templateObject21 = _taggedTemplateLiteral(["\n    label: label_with_icon_left;\n    left: ", ";\n    right: ", ";\n    [dir='rtl'] & {\n      right: ", ";\n      left: ", ";\n    }\n  "])), math("2 * ".concat(sysSpaceMd, " + ").concat(sysSpaceXl)), sysSpaceMd, math("2 * ".concat(sysSpaceMd, " + ").concat(sysSpaceXl)), sysSpaceMd);
  var errorText = css(_templateObject22 || (_templateObject22 = _taggedTemplateLiteral(["\n    label: error_text;\n    ", "\n    align-items: center;\n  "])), info());
  var errorIcon = css(_templateObject23 || (_templateObject23 = _taggedTemplateLiteral(["\n    label: error_icon;\n    color: ", ";\n    margin-right: ", ";\n    margin-left: 0;\n    align-self: flex-start;\n    [dir='rtl'] & {\n      margin-left: ", ";\n      margin-right: 0;\n    }\n  "])), sysColorErrorMain, sysSpaceXs, sysSpaceXs);
  var successIcon = css(_templateObject24 || (_templateObject24 = _taggedTemplateLiteral(["\n    label: success_icon;\n    color: ", ";\n    margin-right: ", ";\n    margin-left: initial;\n    align-self: flex-start;\n    [dir='rtl'] & {\n      margin-left: ", ";\n      margin-right: 0;\n    }\n  "])), sysColorSuccessMain, sysSpaceXs, sysSpaceXs);
  var multiLineError = css(_templateObject25 || (_templateObject25 = _taggedTemplateLiteral(["\n    label: multi_line_error;\n    list-style: none;\n    padding: 0;\n    margin: 0;\n  "])));
  var prefixWrapper = css(_templateObject26 || (_templateObject26 = _taggedTemplateLiteral(["\n    label: prefix_wrapper;\n    position: relative;\n    display: flex;\n    background-color: ", ";\n    &:after {\n      content: ' ';\n      position: absolute;\n      height: 100%;\n      width: 100%;\n      top: 0;\n      left: 0;\n      border: ", " solid\n        ", ";\n      border-radius: ", ";\n      pointer-events: none;\n    }\n    &:focus-within {\n      border-radius: ", ";\n      ", ";\n    }\n    padding: ", ";\n  "])), sysColorBackgroundSurface, sysBorderThicknessNormal, sysColorStructureBorderHighContrast, sysBorderRadiusSm, sysBorderRadiusSm, focusOffsetElement, sysBorderThicknessNormal);
  var prefixWrapperError = css(_templateObject27 || (_templateObject27 = _taggedTemplateLiteral(["\n    label: prefix_wrapper_error;\n    &:after {\n      border: ", " solid ", ";\n    }\n    padding: ", " ", ";\n  "])), sysBorderThicknessEmphasis, sysColorErrorMain, sysBorderThicknessNormal, sysBorderThicknessEmphasis);
  var prefixSuffix = css(_templateObject28 || (_templateObject28 = _taggedTemplateLiteral(["\n    label: prefix_suffix;\n    white-space: nowrap;\n    color: ", ";\n    padding: ", " ", "\n      ", " ", ";\n  "])), sysColorBackgroundHighContrast, math("".concat(backgroundSpaceTop, " + ").concat(sysSpaceSm)), sysSpaceTiny, backgroundSpaceBottomEntered, sysSpaceTiny);
  var PrefixWrapperControl = css(_templateObject29 || (_templateObject29 = _taggedTemplateLiteral(["\n    label: prefix_wrapper_control;\n    border: none;\n    min-height: ", ";\n    &:focus {\n      box-shadow: none;\n      outline: none;\n    }\n    &:placeholder-shown:not(:focus) ~ div {\n      opacity: 0;\n    }\n  "])), ppuiV2CurrencyInputMinHeight);
  var prefix = css(_templateObject30 || (_templateObject30 = _taggedTemplateLiteral(["\n    label: prefix;\n    order: -1;\n    ", "\n    padding-left: ", ";\n    [dir='rtl'] & {\n      ", "\n      padding-right: ", ";\n    }\n  "])), prefixSuffix, sysSpaceMd, prefixSuffix, sysSpaceMd);
  var suffix = css(_templateObject31 || (_templateObject31 = _taggedTemplateLiteral(["\n    label: suffix;\n    ", "\n    padding-right: ", ";\n    [dir='rtl'] & {\n      ", "\n      padding-left: ", ";\n    }\n  "])), prefixSuffix, sysSpaceMd, prefixSuffix, sysSpaceMd);
  var noLabelSuffixOrPrefix = css(_templateObject32 || (_templateObject32 = _taggedTemplateLiteral(["\n    label: no_label_suffix_or_prefix;\n    padding-top: ", ";\n    padding-bottom: ", ";\n    [dir='rtl'] & {\n      padding-top: ", ";\n      padding-bottom: ", ";\n    }\n  "])), math("".concat(backgroundSpaceTop, " - ").concat(sysBorderThicknessNormal)), math("".concat(backgroundSpaceBottom, " - ").concat(sysBorderThicknessNormal)), math("".concat(backgroundSpaceTop, " - ").concat(sysBorderThicknessNormal)), math("".concat(backgroundSpaceBottom, " - ").concat(sysBorderThicknessNormal)));
  var prefixWrapperControlWithPrefix = css(_templateObject33 || (_templateObject33 = _taggedTemplateLiteral(["\n    label: prefix_wrapper_control_with_prefix;\n    padding-left: 0;\n    [dir='rtl'] & {\n      padding-right: 0;\n      padding-left: ", ";\n    }\n  "])), sysSpaceMd);
  var prefixWrapperControlWithSuffix = css(_templateObject34 || (_templateObject34 = _taggedTemplateLiteral(["\n    label: prefix_wrapper_control_with_prefix;\n    padding-right: 0;\n    [dir='rtl'] & {\n      padding-left: 0;\n      padding-right: ", ";\n    }\n  "])), sysSpaceMd);
  var prefixWrapperControlWithPrefixSuffix = css(_templateObject35 || (_templateObject35 = _taggedTemplateLiteral(["\n    label: prefix_wrapper_control_with_prefix_suffix;\n    padding-left: 0;\n    padding-right: 0;\n    [dir='rtl'] & {\n      padding-left: 0;\n      padding-right: 0;\n    }\n  "])));
  /********* TextArea ************/

  var textAreaControl = css(_templateObject36 || (_templateObject36 = _taggedTemplateLiteral(["\n    label: text_area_control;\n    height: auto;\n    background-color: ", ";\n    outline: none;\n    box-shadow: none;\n    resize: none;\n    display: block;\n    &:placeholder-shown:not(:focus) {\n      ~ label {\n        padding-top: 0;\n      }\n    }\n  "])), sysColorBackgroundSurface);
  var textAreaControlLabel = css(_templateObject37 || (_templateObject37 = _taggedTemplateLiteral(["\n    label: text_area_control_label;\n    background-color: ", ";\n    top: ", ";\n    padding-top: ", ";\n  "])), sysColorBackgroundSurface, sysBorderThicknessNormal, math("".concat(sysSpaceSm, " -\n        ").concat(sysBorderThicknessNormal)));
  var textAreaControlErrorLabel = css(_templateObject38 || (_templateObject38 = _taggedTemplateLiteral(["\n    label: text_area_control_error_label;\n    top: ", ";\n    padding-top: ", ";\n  "])), sysBorderThicknessEmphasis, math("".concat(sysSpaceSm, " -\n      ").concat(sysBorderThicknessEmphasis)));
  var textAreaCounterWrapper = css(_templateObject39 || (_templateObject39 = _taggedTemplateLiteral(["\n    label: text_area_counter_wrapper;\n    cursor: text;\n    position: relative;\n    padding: ", ";\n    &:after {\n      content: ' ';\n      position: absolute;\n      height: 100%;\n      width: 100%;\n      top: 0;\n      left: 0;\n      border: ", " solid\n        ", ";\n      border-radius: ", ";\n      pointer-events: none;\n    }\n    textarea:focus,\n    textarea:not(:focus) {\n      border: none;\n      outline: none;\n      box-shadow: none;\n    }\n\n    &:focus-within {\n      border-radius: ", ";\n      ", ";\n    }\n  "])), sysBorderThicknessNormal, sysBorderThicknessNormal, sysColorStructureBorderHighContrast, sysBorderRadiusSm, sysBorderRadiusSm, focusOffsetElement);
  var textAreaCounterText = css(_templateObject40 || (_templateObject40 = _taggedTemplateLiteral(["\n    label: text_area_counter_text;\n    ", "\n    padding: ", "\n      ", "\n      ", "\n      ", ";\n    text-align: right;\n    background-color: ", ";\n    [dir='rtl'] & {\n      text-align: left;\n    }\n  "])), caption({
    color: sysColorBackgroundMediumContrast
  }), sysSpaceSm, math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceSm, " - ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessNormal)), sysColorBackgroundSurface);
  var textAreaErrorCounterWrapper = css(_templateObject41 || (_templateObject41 = _taggedTemplateLiteral(["\n    label: text_area_error_counter_wrapper;\n    padding-left: ", ";\n    padding-right: ", ";\n\n    &:after {\n      border: ", " solid ", ";\n    }\n  "])), sysBorderThicknessEmphasis, sysBorderThicknessEmphasis, sysBorderThicknessEmphasis, sysColorErrorMain);
  var textAreaErrorCounterText = css(_templateObject42 || (_templateObject42 = _taggedTemplateLiteral(["\n    label: text_area_error_counter_text;\n    color: ", ";\n    padding-left: ", ";\n    padding-right: ", ";\n  "])), sysColorErrorMain, math("".concat(sysSpaceMd, " -\n        ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " -\n        ").concat(sysBorderThicknessEmphasis)));
  /********** Text Display ************/

  var ppuiTextInputDisplayRightIconSpaceRight = math("".concat(sysSizeTapTargetDefault, " + ").concat(sysSpaceMd)); // 48px + 12px

  var ppuiTextInputDisplayValueSpaceTop = math("".concat(sysTypographyCaptionLineHeight, " + ").concat(sysSpaceTiny)); // 20px (letter space) + 2px

  // 46px
  var textDisplayNoLabelControl = css(_templateObject43 || (_templateObject43 = _taggedTemplateLiteral(["\n    label: text_display_no_label_control;\n    padding-top: ", ";\n    padding-bottom: ", ";\n    padding-left: 0;\n    [dir='rtl'] & {\n      padding-left: ", ";\n    }\n  "])), sysSpaceMd, sysSpaceMd, ppuiTextInputDisplayRightIconSpaceRight);
  var textDisplayControl = css(_templateObject44 || (_templateObject44 = _taggedTemplateLiteral(["\n    label: text_display_control;\n    border: 0;\n    padding-left: 0;\n    padding-top: ", ";\n    padding-bottom: 0;\n    min-height: ", ";\n    display: block;\n    background-color: transparent;\n    overflow-wrap: break-word;\n    [dir='rtl'] & {\n      padding-right: 0;\n    }\n  "])), ppuiTextInputDisplayValueSpaceTop, '2.875rem');
  var textDisplayLabel = css(_templateObject45 || (_templateObject45 = _taggedTemplateLiteral(["\n    label: text_display_label;\n    top: 0;\n    left: 0;\n    [dir='rtl'] & {\n      left: ", ";\n      right: 0;\n    }\n  "])), ppuiTextInputDisplayRightIconSpaceRight);
  var textDisplayIconRight = css(_templateObject46 || (_templateObject46 = _taggedTemplateLiteral(["\n    label: text_display_icon_right;\n    top: 0;\n  "])));
  var textDisplaylabelIconRight = css(_templateObject47 || (_templateObject47 = _taggedTemplateLiteral(["\n    label: text_display_label_icon_right;\n    right: ", ";\n  "])), ppuiTextInputDisplayRightIconSpaceRight);
  var textDisplayControlIconRight = css(_templateObject48 || (_templateObject48 = _taggedTemplateLiteral(["\n    label: text_display_control_icon_right;\n    padding-right: ", ";\n\n    [dir='rtl'] & {\n      padding-left: ", ";\n    }\n  "])), math("".concat(ppuiTextInputDisplayRightIconSpaceRight, " - ").concat(sysBorderThicknessNormal)), math("".concat(ppuiTextInputDisplayRightIconSpaceRight, " - ").concat(sysBorderThicknessNormal)));
  var placemat = css(_templateObject49 || (_templateObject49 = _taggedTemplateLiteral(["\n    label: placemat;\n    ", "\n    position: absolute;\n    top: 0;\n    right: 0;\n    left: 0;\n    padding: ", "\n      ", "\n      ", "\n      ", ";\n    background-color: ", ";\n    white-space: nowrap;\n    & + input:focus {\n      background-color: transparent;\n    }\n    [dir='rtl'] & {\n      transform: translateX(-", ");\n    }\n  "])), body({
    color: sysColorBackgroundMediumContrast
  }), math("".concat(backgroundSpaceTop, " + ").concat(sysSpaceSm, " + ").concat(sysBorderThicknessNormal)), sysSpaceMd, math("".concat(backgroundSpaceBottomEntered, " + ").concat(sysBorderThicknessNormal)), sysSpaceMd, sysColorBackgroundSurface, sysBorderThicknessNormal);
  var noLabelPlacemat = css(_templateObject50 || (_templateObject50 = _taggedTemplateLiteral(["\n    label: no_label_placemat;\n    padding-top: ", ";\n  "])), backgroundSpaceTop);
  return {
    label: label,
    labelWithScreenreader: labelWithScreenreader,
    helperText: helperText,
    base: base,
    iconRight: iconRight,
    iconLeft: iconLeft,
    control: control,
    nolabelControl: nolabelControl,
    errorControl: errorControl,
    prefixWrapperError: prefixWrapperError,
    controlErrorWithIconRight: controlErrorWithIconRight,
    controlErrorWithIconLeft: controlErrorWithIconLeft,
    controlErrorWithIconLeftAndRight: controlErrorWithIconLeftAndRight,
    noLabelControlError: noLabelControlError,
    placemat: placemat,
    noLabelPlacemat: noLabelPlacemat,
    controlWithIconRight: controlWithIconRight,
    controlWithIconLeft: controlWithIconLeft,
    controlWithIconLeftAndRight: controlWithIconLeftAndRight,
    labelWithIconRight: labelWithIconRight,
    labelWithIconLeft: labelWithIconLeft,
    errorText: errorText,
    errorIcon: errorIcon,
    successIcon: successIcon,
    multiLineError: multiLineError,
    prefixWrapper: prefixWrapper,
    prefixSuffix: prefixSuffix,
    PrefixWrapperControl: PrefixWrapperControl,
    prefix: prefix,
    suffix: suffix,
    noLabelSuffixOrPrefix: noLabelSuffixOrPrefix,
    prefixWrapperControlWithPrefix: prefixWrapperControlWithPrefix,
    prefixWrapperControlWithSuffix: prefixWrapperControlWithSuffix,
    prefixWrapperControlWithPrefixSuffix: prefixWrapperControlWithPrefixSuffix,
    labelWhenPlaceholderShownAndInputNotFocused: labelWhenPlaceholderShownAndInputNotFocused,
    textAreaControl: textAreaControl,
    textAreaControlLabel: textAreaControlLabel,
    textAreaControlErrorLabel: textAreaControlErrorLabel,
    textAreaCounterWrapper: textAreaCounterWrapper,
    textAreaCounterText: textAreaCounterText,
    textAreaErrorCounterWrapper: textAreaErrorCounterWrapper,
    textAreaErrorCounterText: textAreaErrorCounterText,
    textDisplayNoLabelControl: textDisplayNoLabelControl,
    textDisplayControl: textDisplayControl,
    textDisplayLabel: textDisplayLabel,
    textDisplayIconRight: textDisplayIconRight,
    textDisplaylabelIconRight: textDisplaylabelIconRight,
    textDisplayControlIconRight: textDisplayControlIconRight
  };
}

var _excluded = ["setFocus", "className", "inputClassName", "labelClassName", "successText", "successIconAriaLabel", "errorText", "error", "errorIconAriaLabel", "helperText", "value", "name", "id", "label", "isLabelHidden", "placeholder", "placemat", "leftIcon", "rightIcon", "onChange", "children", "renderContent", "removeBorderRadiusLeft", "removeBorderRadiusRight", "removeBorderRadiusBoth", "dir", "leftIconCSS", "rightIconCSS"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

var FormMessage = function (_ref) {
  var messageId = _ref.messageId,
      helperText = _ref.helperText,
      errorText = _ref.errorText,
      errorIconAriaLabel = _ref.errorIconAriaLabel,
      successText = _ref.successText,
      successIconAriaLabel = _ref.successIconAriaLabel;
  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]); // errorText takes higher priority over helperText


  if (errorText || successText) {
    var _errorIcon;

    var text = errorText ? errorText : successText;
    var processText = Array.isArray(text) ? jsx("ul", {
      css: _styles.multiLineError,
      "data-ppui": "true"
    }, text.map(function (msg, index) {
      return jsx("li", {
        key: "msg".concat(messageId, "-").concat(index),
        "data-ppui": "true"
      }, msg);
    })) : text;
    var errorIcon, successIcon;

    if (errorText) {
      var errorProps = {
        css: _styles.errorIcon,
        role: 'img',
        'aria-label': errorIconAriaLabel
      };
      errorIcon = jsx(CriticalIcon, _extends({
        size: "sm"
      }, errorProps, {
        "data-ppui": "true"
      }));
    }

    if (!errorText) {
      var successProps = {
        css: _styles.successIcon,
        role: 'img',
        'aria-label': successIconAriaLabel
      };
      successIcon = jsx(CheckmarkBackgroundIcon, _extends({
        size: "sm"
      }, successProps, {
        "data-ppui": "true"
      }));
    }

    return jsx("div", {
      css: _styles.errorText,
      id: messageId,
      role: "alert",
      "data-ppui": "true"
    }, (_errorIcon = errorIcon) !== null && _errorIcon !== void 0 ? _errorIcon : successIcon, processText);
  }

  if (helperText) {
    var helperTextCssVal = [_styles.helperText];

    var _processText = Array.isArray(helperText) ? jsx("ul", {
      css: helperTextCssVal,
      id: messageId,
      "data-ppui": "true"
    }, helperText.map(function (msg, index) {
      return jsx("li", {
        key: "msg".concat(messageId, "-").concat(index),
        "data-ppui": "true"
      }, msg);
    })) : jsx("div", {
      css: helperTextCssVal,
      id: messageId,
      "data-ppui": "true"
    }, helperText);

    return _processText;
  }

  return null;
};

var renderIcon = function (icon, side, customIconCSS, _styles) {
  return icon ? jsx("div", {
    css: [side === 'left' && _styles.iconLeft, side === 'right' && _styles.iconRight, customIconCSS],
    key: "icon-".concat(side),
    "data-ppui": "true"
  }, icon) : null;
};

var TextInputWrapper = function (_ref2) {
  var setFocus = _ref2.setFocus,
      className = _ref2.className,
      inputClassName = _ref2.inputClassName,
      labelClassName = _ref2.labelClassName,
      successText = _ref2.successText,
      _ref2$successIconAria = _ref2.successIconAriaLabel,
      successIconAriaLabel = _ref2$successIconAria === void 0 ? 'Success' : _ref2$successIconAria,
      errorText = _ref2.errorText,
      _ref2$error = _ref2.error,
      error = _ref2$error === void 0 ? !!errorText : _ref2$error,
      _ref2$errorIconAriaLa = _ref2.errorIconAriaLabel,
      errorIconAriaLabel = _ref2$errorIconAriaLa === void 0 ? 'Error' : _ref2$errorIconAriaLa,
      helperText = _ref2.helperText,
      value = _ref2.value,
      name = _ref2.name,
      _ref2$id = _ref2.id,
      id = _ref2$id === void 0 ? "text-input-".concat(name) : _ref2$id,
      label = _ref2.label,
      isLabelHidden = _ref2.isLabelHidden,
      _ref2$placeholder = _ref2.placeholder,
      placeholder = _ref2$placeholder === void 0 ? ' ' : _ref2$placeholder,
      placemat = _ref2.placemat,
      _ref2$leftIcon = _ref2.leftIcon,
      leftIcon = _ref2$leftIcon === void 0 ? null : _ref2$leftIcon,
      _ref2$rightIcon = _ref2.rightIcon,
      rightIcon = _ref2$rightIcon === void 0 ? null : _ref2$rightIcon,
      onChange = _ref2.onChange,
      children = _ref2.children,
      renderContent = _ref2.renderContent,
      removeBorderRadiusLeft = _ref2.removeBorderRadiusLeft,
      removeBorderRadiusRight = _ref2.removeBorderRadiusRight,
      removeBorderRadiusBoth = _ref2.removeBorderRadiusBoth,
      dir = _ref2.dir,
      leftIconCSS = _ref2.leftIconCSS,
      rightIconCSS = _ref2.rightIconCSS,
      rest = _objectWithoutProperties(_ref2, _excluded);

  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var mainCSS = [_styles.base];
  var msgId = "message_".concat(id);
  var valueProps = onChange ? {
    onChange: onChange,
    value: value
  } : {
    defaultValue: value
  };
  var ariaDescribedByProp = helperText || errorText ? {
    'aria-describedby': msgId
  } : {};

  var otherProps = _objectSpread(_objectSpread(_objectSpread({}, ariaDescribedByProp), valueProps), rest);

  return jsx("div", {
    className: className,
    css: mainCSS,
    dir: dir,
    "data-ppui-info": "text-input_5.4.4"
  }, renderIcon(leftIcon, 'left', leftIconCSS, _styles), renderContent(_objectSpread({
    inputClassName: inputClassName,
    name: name,
    id: id,
    setFocus: setFocus,
    hasError: error,
    placeholder: placeholder,
    placemat: placemat,
    label: label,
    labelClassName: labelClassName,
    isLabelHidden: isLabelHidden,
    inputTag: 'input',
    leftIcon: leftIcon,
    rightIcon: rightIcon
  }, otherProps)), jsx(FormMessage, {
    errorText: errorText,
    errorIconAriaLabel: errorIconAriaLabel,
    successText: successText,
    successIconAriaLabel: successIconAriaLabel,
    helperText: helperText,
    messageId: msgId,
    "data-ppui": "true"
  }), renderIcon(rightIcon, 'right', rightIconCSS, _styles));
};

var _excluded$1 = ["inputClassName", "inputTag", "name", "id", "setFocus", "inputRef", "hasError", "placeholder", "placemat", "label", "labelClassName", "isLabelHidden", "rightIcon", "leftIcon", "prefix", "suffix"];

var overlayValue = function () {
  var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var mask = arguments.length > 1 ? arguments[1] : undefined;
  var vLen = value.length;
  var mLen = mask.length;

  if (vLen >= mLen) {
    return value;
  }

  return value.concat(mask.substring(vLen));
};

var Placemat = function (_ref) {
  var placeholder = _ref.placeholder,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? '' : _ref$value,
      _ref$isLabelHidden = _ref.isLabelHidden,
      isLabelHidden = _ref$isLabelHidden === void 0 ? false : _ref$isLabelHidden,
      _styles = _ref.styles;
  var combinedText = overlayValue(value, placeholder);
  var cssVal = [_styles.placemat, isLabelHidden && _styles.noLabelPlacemat];
  return jsx("div", {
    css: cssVal,
    "data-ppui": "true"
  }, combinedText);
};

var normalizePlaceholder = function (placeholder) {
  var normalized = placeholder !== null && placeholder !== void 0 ? placeholder : ' ';
  return normalized === '' ? ' ' : normalized;
}; // eslint-disable-next-line complexity


var TextInputContent = function (_ref2) {
  var inputClassName = _ref2.inputClassName,
      inputTag = _ref2.inputTag,
      name = _ref2.name,
      id = _ref2.id,
      setFocus = _ref2.setFocus,
      inputRef = _ref2.inputRef,
      hasError = _ref2.hasError,
      placeholder = _ref2.placeholder,
      placemat = _ref2.placemat,
      label = _ref2.label,
      labelClassName = _ref2.labelClassName,
      isLabelHidden = _ref2.isLabelHidden,
      rightIcon = _ref2.rightIcon,
      leftIcon = _ref2.leftIcon,
      prefix = _ref2.prefix,
      suffix = _ref2.suffix,
      rest = _objectWithoutProperties(_ref2, _excluded$1);

  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var isLabelHiddenFinal = !label || label === '' || isLabelHidden; // DO NOT CHANGE THE ORDER OF THE STYLES BELOW

  var inputTagCSS = [_styles.control, isLabelHiddenFinal && _styles.nolabelControl, rightIcon && _styles.controlWithIconRight, leftIcon && _styles.controlWithIconLeft, rightIcon && leftIcon && _styles.controlWithIconLeftAndRight, _styles.labelWhenPlaceholderShownAndInputNotFocused, hasError && _styles.errorControl, hasError && isLabelHiddenFinal && _styles.noLabelControlError, hasError && rightIcon && _styles.controlErrorWithIconRight, hasError && leftIcon && _styles.controlErrorWithIconLeft, hasError && leftIcon && rightIcon && _styles.controlErrorWithIconLeftAndRight, prefix || suffix ? _styles.PrefixWrapperControl : null, hasError && isLabelHiddenFinal && (prefix || suffix) ? _styles.noLabelControlError : null, prefix && _styles.prefixWrapperControlWithPrefix, suffix && _styles.prefixWrapperControlWithSuffix, prefix && suffix && _styles.prefixWrapperControlWithPrefixSuffix];
  var labelCSS = [_styles.label, rightIcon && _styles.labelWithIconRight, leftIcon && _styles.labelWithIconLeft, isLabelHiddenFinal && _styles.labelWithScreenreader];
  // @ts-ignore
  var value = rest.value;
  useEffect(function () {
    var elem = inputRef.current;

    if (setFocus && elem) {
      elem.focus();
    }
  }, [setFocus]);
  return jsx(React.Fragment, null, !!placemat && jsx(Placemat, {
    placeholder: placemat,
    value: value,
    isLabelHidden: isLabelHiddenFinal,
    styles: _styles,
    "data-ppui": "true"
  }), jsx(inputTag, _extends({
    className: inputClassName,
    css: inputTagCSS,
    name: name,
    id: id,
    ref: inputRef,
    "aria-invalid": hasError,
    placeholder: normalizePlaceholder(placeholder)
  }, rest, {
    "data-ppui": "true"
  })), label && jsx("label", {
    htmlFor: id,
    id: "".concat(id, "-label"),
    className: labelClassName,
    css: labelCSS,
    "data-ppui": "true"
  }, label));
};

var _excluded$2 = ["label", "id", "prefix", "suffix", "prefixClassName", "suffixClassName", "inputClassName", "inputRef", "onBlur", "onFocus", "hasError", "isLabelHidden"];

var TextInputContentWithPrefixSuffix = function (_ref) {
  var label = _ref.label,
      id = _ref.id,
      prefix = _ref.prefix,
      suffix = _ref.suffix,
      prefixClassName = _ref.prefixClassName,
      suffixClassName = _ref.suffixClassName,
      inputClassName = _ref.inputClassName,
      inputRef = _ref.inputRef,
      _ref$onBlur = _ref.onBlur,
      onBlur = _ref$onBlur === void 0 ? null : _ref$onBlur,
      _ref$onFocus = _ref.onFocus,
      onFocus = _ref$onFocus === void 0 ? null : _ref$onFocus,
      hasError = _ref.hasError,
      isLabelHidden = _ref.isLabelHidden,
      rest = _objectWithoutProperties(_ref, _excluded$2);

  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var textInputRef = inputRef;
  var isLabelHiddenFinal = !label || label === '' || isLabelHidden;
  var prefixClass = [_styles.prefix, isLabelHiddenFinal && _styles.noLabelSuffixOrPrefix];
  var suffixClass = [_styles.suffix, isLabelHiddenFinal && _styles.noLabelSuffixOrPrefix];
  var prefixWrapperClass = [_styles.prefixWrapper, hasError && _styles.prefixWrapperError];
  var labelId = label ? "".concat(id, "-label ") : '';
  var prefixEleId = prefix ? "".concat(id, "-prefix ") : '';
  var suffixEleId = suffix ? "".concat(id, "-suffix") : '';
  var ariaLabelledByIds = "".concat(labelId).concat(prefixEleId).concat(suffixEleId);
  return jsx("div", {
    css: prefixWrapperClass,
    onClick: function handlePrefixWrapperClick() {
      /* istanbul ignore else */
      if (textInputRef.current) {
        var textInputElm = textInputRef.current;
        textInputElm.focus();
      }
    },
    "data-ppui": "true"
  }, jsx(TextInputContent, _extends({
    label: label,
    id: id,
    prefix: prefix,
    suffix: suffix,
    inputClassName: inputClassName,
    inputRef: inputRef,
    onFocus: function handleFocus(event) {
      /* istanbul ignore else */
      if (onFocus) {
        onFocus(event);
      }
    },
    onBlur: function handleBlur(event) {
      /* istanbul ignore else */
      if (onBlur) {
        onBlur(event);
      }
    },
    "aria-labelledby": ariaLabelledByIds.trim(),
    hasError: hasError,
    isLabelHidden: isLabelHiddenFinal
  }, rest, {
    "data-ppui": "true"
  })), prefix && jsx("div", {
    id: prefixEleId.trim(),
    css: prefixClass,
    className: prefixClassName,
    "data-ppui": "true"
  }, prefix), suffix && jsx("div", {
    id: suffixEleId.trim(),
    css: suffixClass,
    className: suffixClassName,
    "data-ppui": "true"
  }, suffix));
};

var _excluded$3 = ["prefix", "suffix", "prefixClassName", "suffixClassName"],
    _excluded2 = ["inputClassName", "name", "id", "setFocus", "hasError", "placeholder", "placemat", "label", "labelClassName", "inputTag"];
var TextInput = forwardRef(function (_ref, ref) {
  var prefix = _ref.prefix,
      suffix = _ref.suffix,
      prefixClassName = _ref.prefixClassName,
      suffixClassName = _ref.suffixClassName,
      props = _objectWithoutProperties(_ref, _excluded$3);

  var internalRef = useRef();
  var textInputRef = ref || internalRef;
  return /*#__PURE__*/React.createElement(TextInputWrapper, _extends({
    renderContent: function renderContent(_ref2) {
      var inputClassName = _ref2.inputClassName,
          name = _ref2.name,
          id = _ref2.id,
          setFocus = _ref2.setFocus,
          hasError = _ref2.hasError,
          placeholder = _ref2.placeholder,
          placemat = _ref2.placemat,
          label = _ref2.label,
          labelClassName = _ref2.labelClassName,
          inputTag = _ref2.inputTag,
          otherProps = _objectWithoutProperties(_ref2, _excluded2);

      if (prefix || suffix) {
        return /*#__PURE__*/React.createElement(TextInputContentWithPrefixSuffix, _extends({
          inputClassName: inputClassName,
          inputTag: inputTag,
          name: name,
          id: id,
          setFocus: setFocus,
          inputRef: textInputRef,
          hasError: hasError,
          placeholder: placeholder
          /*placemat={placemat}  placemat is currently not supported with prefix and suffix*/
          ,
          label: label,
          labelClassName: labelClassName,
          prefix: prefix,
          suffix: suffix,
          prefixClassName: prefixClassName,
          suffixClassName: suffixClassName
        }, otherProps, {
          "data-ppui": "true"
        }));
      }

      return /*#__PURE__*/React.createElement(TextInputContent, _extends({
        inputClassName: inputClassName,
        inputTag: inputTag,
        name: name,
        id: id,
        setFocus: setFocus,
        inputRef: textInputRef,
        hasError: hasError,
        placeholder: placeholder,
        placemat: placemat,
        label: label,
        labelClassName: labelClassName
      }, otherProps, {
        "data-ppui": "true"
      }));
    }
  }, props, {
    "data-ppui": "true"
  }));
});

var _excluded$4 = ["className", "minRows", "maxRows", "showCharacterCounter", "maxCharacters", "counterStatusText", "onChange", "onBlur", "onFocus", "value", "errorText", "successText"],
    _excluded2$1 = ["inputClassName", "name", "id", "setFocus", "hasError", "placeholder", "label", "labelClassName", "inputTag", "placemat"];
var MIN_ROWS = 2;
var MAX_ROWS = 6;
var TextArea = forwardRef(function (_ref, ref) {
  var className = _ref.className,
      _ref$minRows = _ref.minRows,
      minRows = _ref$minRows === void 0 ? MIN_ROWS : _ref$minRows,
      _ref$maxRows = _ref.maxRows,
      maxRows = _ref$maxRows === void 0 ? MAX_ROWS : _ref$maxRows,
      _ref$showCharacterCou = _ref.showCharacterCounter,
      showCharacterCounter = _ref$showCharacterCou === void 0 ? false : _ref$showCharacterCou,
      maxCharacters = _ref.maxCharacters,
      counterStatusText = _ref.counterStatusText,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? null : _ref$onChange,
      _ref$onBlur = _ref.onBlur,
      onBlur = _ref$onBlur === void 0 ? null : _ref$onBlur,
      _ref$onFocus = _ref.onFocus,
      onFocus = _ref$onFocus === void 0 ? null : _ref$onFocus,
      value = _ref.value,
      errorText = _ref.errorText,
      successText = _ref.successText,
      rest = _objectWithoutProperties(_ref, _excluded$4);

  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var textLineHeight = remToPx( // @ts-ignore
  tokens.sysTypographyFieldLabelLgLineHeight).split('px')[0];
  var maxAllowedRows = maxRows ? Math.min(maxRows, MAX_ROWS) : MAX_ROWS;
  var minAllowedRows = minRows ? Math.max(minRows, MIN_ROWS) : MIN_ROWS;

  var _useState = useState(value ? value.length : 0),
      _useState2 = _slicedToArray(_useState, 2),
      charCount = _useState2[0],
      setCharCount = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isRTL = _useState4[0],
      setRTL = _useState4[1];

  var internalRef = useRef();
  var textAreaRef = ref || internalRef;
  var intlContext = useContext(IntlContext);
  var bordersOffset = useRef(0);
  var maxHeight = useRef(textLineHeight * maxAllowedRows);
  var minHeight = useRef(textLineHeight * minAllowedRows);

  var computeHeight = function () {
    var textAreaElm = textAreaRef.current; // eslint-disable-next-line

    textAreaElm.style.height = 'auto';
    var newHeight = textAreaElm.scrollHeight + bordersOffset.current;
    /* istanbul ignore next */

    if (newHeight > maxHeight.current) {
      textAreaElm.style.height = "".concat(maxHeight.current, "px");
    } else if (newHeight > minHeight.current) {
      textAreaElm.style.height = "".concat(newHeight, "px");
    } else {
      textAreaElm.style.height = "".concat(minHeight.current, "px");
    }
  };

  useEffect(function () {
    /* istanbul ignore next */
    if (textAreaRef.current) {
      var textAreaElm = textAreaRef.current;
      var offsetHeight = textAreaElm.offsetHeight,
          clientHeight = textAreaElm.clientHeight;
      var computedStyle = window.getComputedStyle(textAreaElm);
      var lineHeightValue = Number(computedStyle.getPropertyValue('line-height').split('px')[0]);
      var paddingBottomValue = Number(computedStyle.getPropertyValue('padding-bottom').split('px')[0]);
      var paddingTopValue = Number(computedStyle.getPropertyValue('padding-top').split('px')[0]);
      var lineHeight = Number.isNaN(lineHeightValue) ? textLineHeight : lineHeightValue;
      var paddingBottom = Number.isNaN(paddingBottomValue) ? 0 : paddingBottomValue;
      var paddingTop = Number.isNaN(paddingTopValue) ? 0 : paddingTopValue;
      bordersOffset.current = offsetHeight - clientHeight;
      maxHeight.current = bordersOffset.current + paddingTop + paddingBottom + lineHeight * maxAllowedRows;
      minHeight.current = bordersOffset.current + paddingTop + paddingBottom + lineHeight * minAllowedRows;
      computeHeight();
      setRTL(computedStyle.direction === 'rtl');
    }
  }, [minAllowedRows, maxAllowedRows, value]);

  var handleCounterWrapperClick = function () {
    /* istanbul ignore else */
    if (textAreaRef.current) {
      var textAreaElm = textAreaRef.current;
      textAreaElm.focus();
    }
  };

  return jsx(TextInputWrapper, _extends({
    className: className,
    value: value,
    onChange: function handleChange(event) {
      var textAreaElm = event.target;
      setCharCount(textAreaElm.value.length);
      /* istanbul ignore else */

      if (onChange) {
        onChange(event);
      }
    },
    onFocus: function handleFocus(event) {
      /* istanbul ignore else */
      if (onFocus) {
        onFocus(event);
      }
    },
    onBlur: function handleBlur(event) {
      /* istanbul ignore else */
      if (onBlur) {
        onBlur(event);
      }
    },
    renderContent: function renderContent(_ref2) {
      var inputClassName = _ref2.inputClassName,
          name = _ref2.name,
          id = _ref2.id,
          setFocus = _ref2.setFocus,
          hasError = _ref2.hasError,
          placeholder = _ref2.placeholder,
          label = _ref2.label,
          labelClassName = _ref2.labelClassName,
          inputTag = _ref2.inputTag,
          placemat = _ref2.placemat,
          otherProps = _objectWithoutProperties(_ref2, _excluded2$1);

      var textInputContent = jsx(ClassNames, {
        "data-ppui": "true"
      }, function (_ref3) {
        var css = _ref3.css,
            cx = _ref3.cx;
        return jsx(TextInputContent, _extends({
          inputClassName: cx(inputClassName, _defineProperty({}, css(_styles.textAreaControl), true)),
          inputTag: "textarea",
          name: name,
          id: id,
          setFocus: setFocus,
          inputRef: textAreaRef,
          hasError: hasError,
          placeholder: placeholder,
          label: label,
          labelClassName: cx(labelClassName, _defineProperty({}, css(_styles.textAreaControlLabel), true), _defineProperty({}, css(_styles.textAreaControlErrorLabel), hasError))
        }, otherProps, {
          "data-ppui": "true"
        }));
      });

      if (showCharacterCounter) {
        var maxCharsText = '';

        if (maxCharacters) {
          maxCharsText = isRTL ? "".concat(maxCharacters, "/") : "/".concat(maxCharacters);
        }

        var counterText = isRTL ? "".concat(maxCharsText).concat(charCount) : "".concat(charCount).concat(maxCharsText);
        var counterStatusMessage = getFormattedMessage(intlContext, "".concat(charCount, " out of ").concat(maxCharacters, " characters used"), counterStatusText, {
          charCount: charCount,
          maxCharacters: maxCharacters
        });
        var wrapperCSS = [_styles.textAreaCounterWrapper, hasError && _styles.textAreaErrorCounterWrapper];
        var counterTextCSS = [_styles.textAreaCounterText, hasError && _styles.textAreaErrorCounterText];
        return jsx("div", {
          css: wrapperCSS,
          onClick: handleCounterWrapperClick,
          "data-ppui": "true"
        }, textInputContent, jsx("div", {
          css: counterTextCSS,
          role: "status",
          "aria-live": "polite",
          "aria-atomic": "true",
          "aria-label": counterStatusMessage,
          "data-ppui": "true"
        }, counterText));
      }

      return textInputContent;
    },
    errorText: errorText,
    successText: successText,
    "data-ppui-info": "text-area_5.4.4"
  }, rest));
});

var _excluded$5 = ["className", "valueClassName", "labelClassName", "name", "id", "label", "value", "rightIcon", "helperText", "isLabelHidden"];

var TextDisplay = function (_ref) {
  var className = _ref.className,
      valueClassName = _ref.valueClassName,
      labelClassName = _ref.labelClassName,
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? 'textDisplay' : _ref$name,
      id = _ref.id,
      _ref$label = _ref.label,
      label = _ref$label === void 0 ? 'TextDisplay' : _ref$label,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? '' : _ref$value,
      rightIcon = _ref.rightIcon,
      helperText = _ref.helperText,
      isLabelHidden = _ref.isLabelHidden,
      rest = _objectWithoutProperties(_ref, _excluded$5);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var controlStyles = [_styles.control, _styles.textDisplayControl, rightIcon && _styles.textDisplayControlIconRight];
  var labelStyles = [_styles.label, _styles.textDisplayLabel, rightIcon && _styles.textDisplaylabelIconRight, isLabelHidden && _styles.textDisplayNoLabelControl, isLabelHidden && _styles.labelWithScreenreader];
  var labelId = "txtDsplyLbl_".concat(name);
  var valueId = "txtDsplyVlu_".concat(name);
  return jsx(TextInputWrapper, _extends({
    className: className,
    label: label,
    name: name,
    isLabelHidden: isLabelHidden,
    helperText: helperText,
    renderContent: function renderContent() {
      return jsx("div", {
        "aria-labelledby": "".concat(labelId, " ").concat(valueId),
        id: id,
        "data-ppui-info": "text-display_5.4.4"
      }, jsx("input", {
        type: "hidden",
        name: name,
        value: value,
        "data-ppui": "true"
      }), jsx("span", {
        css: controlStyles,
        className: valueClassName,
        id: valueId,
        "data-ppui": "true"
      }, value), jsx("span", {
        css: labelStyles,
        className: labelClassName,
        id: labelId,
        "data-ppui": "true"
      }, label));
    },
    rightIcon: rightIcon,
    rightIconCSS: _styles.textDisplayIconRight
  }, rest, {
    "data-ppui": "true"
  }));
};

export { TextArea, TextDisplay, TextInput, styles as textInputStyles };
